import { Avatar, Button, Image, Input, Space, message } from "antd";
import { UserOutlined, CameraOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import DataForm from "../../components/entityManager/dataform";
import { enmControlType, enmPropertyType, IProperty } from "../../components/entityManager/interfaces";
import { BaseComponent, IBaseProps, IBaseStates } from "../../components/infrastructure/baseComponent";
import { Core } from "../../components/infrastructure/core";
import { enmEntityState, ICallOptions, IResultSet } from "../../components/infrastructure/models";
import { IUser } from "../user/interfaces";

export interface IUserEditProps extends IBaseProps {
    currentUser: IUser;
}

export interface IUserEditStates extends IBaseStates {
    currentUser: IUser;
    file?: string;
    fileStream?: any;
}

export class UserEdit extends BaseComponent<IUserEditProps, IUserEditStates> {
    properties: IProperty[];
    inputFile?: Input | null;
    whiteList: string = 'image/png,image/jpg,image/jpeg';
    whiteListExtensions: string[] = ".png,.jpg,.jpeg".split(",");
    constructor(props: IUserEditProps) {
        super(props)
        this.properties = [
            {
                title: "نام کاربری",
                dataIndex: "UserName",
                propertyType: enmPropertyType.string,
                controlType: enmControlType.textbox,
                filterable: true,
                width: 150,
                disabled: true,
                rules: [{ required: true, message: 'لطفا نام کاربری را وارد نمایید' }]
            },
            {
                title: "نام",
                dataIndex: "FirstName",
                propertyType: enmPropertyType.string,
                controlType: enmControlType.textbox,
                filterable: true,
                width: 150,
                rules: [{ required: true, message: 'لطفا نام را وارد نمایید' }]
            },
            {
                title: "نام خانوادگی",
                dataIndex: "LastName",
                propertyType: enmPropertyType.string,
                controlType: enmControlType.textbox,
                filterable: true,
                width: 150,
                rules: [{ required: true, message: 'لطفا نام خانوادگی را وارد نمایید' }]
            },
            {
                title: "ایمیل",
                dataIndex: "Email",
                propertyType: enmPropertyType.string,
                controlType: enmControlType.email,
                filterable: true,
                width: 150
            },
            {
                title: "شماره تلفن همراه",
                dataIndex: "Phone",
                propertyType: enmPropertyType.string,
                controlType: enmControlType.textbox,
                filterable: true,
                rules: [
                    { len: 11, message: 'شماره موبایل باید 11 رقم باشد' }
                ],
                width: 150
            },
            {
                title: "تصویر پروفایل",
                dataIndex: "ProfilePicture",
                showColumn: false,
                controlType: enmControlType.template,
                filterable: false,
                renderControl: (_: IUser) => {
                    const mask: React.ReactNode = (
                        <>
                            <Button style={{ backgroundColor: 'transparent', border: 'none' }} icon={<EyeOutlined />} size={"large"} title="نمایش" />
                            <Button style={{ backgroundColor: 'transparent', border: 'none' }} icon={<DeleteOutlined />} size={"large"} title="حذف"
                                onClick={() => {
                                    let currentUser = this.state.currentUser;
                                    currentUser.ProfilePicturePath = undefined;
                                    this.setFile(currentUser);
                                }} />
                        </>
                    );
                    return (
                        <Space direction="vertical">
                            <div style={{ textAlign: "center" }}>
                                {this.state.file || this.state.currentUser?.ProfilePicturePath
                                    ? <Avatar src={<Image preview={{ mask }} src={this.state.file ?? this.state.currentUser?.ProfilePicturePath} style={{ height: 100 }} />} size={100} />
                                    : <Avatar icon={<UserOutlined style={{ verticalAlign: 'text-top', height: 100 }} />} size={100} />
                                }
                            </div>

                            <Button onClick={this.browseFile} icon={<CameraOutlined style={{ verticalAlign: 'middle' }} />} style={{}} >
                                تغییر تصویر
                            </Button>
                            <Input type="file" hidden ref={node => this.inputFile = node} onChange={this.changeFile} accept={this.whiteList} />

                        </Space>
                    );
                }
            }
        ];

        const { currentUser } = this.props;
        const { Phone } = currentUser;
        this.state = {
            loading: false,
            isAuthorized: true,
            currentUser: { ...currentUser, Phone: Phone ? '0' + Phone : '' },
            windowSize: window.innerWidth <= 765 ? 1 : 2
        };
    }
    browseFile = () => {
        this.inputFile?.input.click();
    }
    changeFile = async (event: any) => {
        let fileStream = event.target.files.length ? event.target.files[0] : undefined;
        if (!fileStream || this.whiteList.split(',').indexOf(fileStream.type) === -1 || !fileStream.name || fileStream.name === '' || fileStream.name.split('.').length > 2 || fileStream.name.indexOf('\0') > -1 || this.whiteListExtensions.filter(f => fileStream.name.toLowerCase().endsWith(f)).length === 0) {
            message.error(`فایل انتخاب شده غیرمجاز است`);
            return false;
        }
        if (fileStream.size > 2097152) {
            message.error(`حداکثر حجم فایل 2 مگابایت باشد`);
            return false;
        }
        let file = fileStream ? await Core.getBase64(fileStream) : undefined;
        let currentUser = this.state.currentUser;
        this.setFile(currentUser, file, fileStream);
    }
    setFile(currentUser: IUser, file?: string, fileStream?: any) {
        if (currentUser && currentUser.ModifiedProperties.indexOf("ProfilePicturePath") === -1) {
            currentUser.ModifiedProperties.push("ProfilePicturePath");
            currentUser.State = enmEntityState.Modified;
        }
        this.setState({ file, fileStream, currentUser });
    }
    async save(entity: IUser): Promise<boolean> {
        if (!entity)
            return false;

        let formData = new FormData();
        if (this.state.fileStream)
            formData.append("Avatar", this.state.fileStream);

        if (entity.State === enmEntityState.Modified || entity.State === enmEntityState.Added) {
            for (var i = 0; i < entity.ModifiedProperties.length; i++) {
                let val = entity.ModifiedProperties[i];
                formData.append(val, (entity as any)[val]);
            }
            formData.append("ModifiedProperties", entity.ModifiedProperties.join(','));
        }
        if (entity.State === enmEntityState.Unchanged) {
            Core.warningNotification('لطفا اطلاعات را اصلاح سپس ذخیره نمایید');
            return false;
        }
        formData.append("RowGuid", entity.RowGuid);
        formData.append("Id", entity.Id.toString());
        formData.append("RowVrsn", entity.RowVrsn.toString());
        formData.append("State", entity.State.toString());

        let fetchOpt: ICallOptions = {
            url: "user/editprofile",
            data: formData,
            headers: [{ key: "encType", value: "multipart/form-data" }],
            contentType: false,
            onSuccess: (result) => {
                if (result.ValidationState.Successful) {
                    Core.successNotification('تغییرات با موفقیت ذخیره شد');
                    let res: IResultSet<IUser[]> = result as any;
                    let user = res && res.Data && res.Data[0];
                    user && this.setState({ currentUser: user });
                }
            }
        };
        return await this.callApi(fetchOpt);
    }

    virtualRender() {
        return (
            <DataForm<IUser>
                parent={this}
                style={{ boxShadow: "inherit" }}
                resetable={false}
                currentEntity={this.state.currentUser}
                properties={this.properties}
                onSubmit={() => this.save(this.state.currentUser)}
                onChange={(property, value) => {
                    let currentUser = this.state.currentUser as any;
                    if (currentUser) {
                        currentUser[property.dataIndex!] = value;
                        if (!this.state.currentUser?.ModifiedProperties.find(i => i === property.dataIndex!))
                            currentUser.ModifiedProperties.push(property.dataIndex!);
                        if (currentUser.State !== enmEntityState.Added)
                            currentUser.State = enmEntityState.Modified;
                    }
                    this.setState({ currentUser });
                }} />
        );
    }
}